import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAppointmentsCalendar,
  getAvailableDocumentsToAdd,
  getDocuments,
} from "../../../actions/accidentDashboard";
import { getAccidents } from "../../../actions/accidents";
import { clearIdTurnos } from "../../../actions/appointments";
import {
  correctConnectivityErrorAction,
  getAllConnectivityErrors,
  getConnectivityErrors,
} from "../../../actions/connectivityResponse";
import {
  clearIdTurnosForms,
  getForm,
  getFormDiagnosis,
  getFormModel,
  getFormNaturalezasLesion,
  getFormPrestaciones,
  getFormProfessionals,
  getFormProfessionalSpecialties,
  getFormSpecialtiesDiagnosis,
  getFormZonasAfectadas,
} from "../../../actions/forms";
import {
  closeForm,
  openEditForm,
  openNewForm,
  setErrorLoadingForm,
  setFormsGeneralProperties,
  setLoadedForm,
} from "../../../actions/formsState";
import snackbarUtils from "../../../utils/snackbarUtils";
import { formBehaviourEnum } from "../../commons/enum/formEnums";
import Form from "./form";

/** Solo puede haber uno de estos componentes a la vez por pantalla. Para abrir distintos se usan las acciones. openNewForm y openEditForm
 *
 * Para mas información de la implementacion del componente y como utilizarlo:
 * https://aisrl.sharepoint.com/sites/desarrollo/_layouts/OneNote.aspx?id=%2Fsites%2Fdesarrollo%2FSiteAssets%2FBloc%20de%20notas%20de%20Desarrollo&wd=target%28ML%20Point.one%7C9860A33D-0D30-4392-BB53-700F7E4BBAAE%2F1.%20Forms%7C79042C1E-1675-4422-8431-442C7F94C94B%2F%29
 */
const FormContainer = ({ isDialog = true }) => {
  const dispatch = useDispatch();
  const formsState = useSelector((state) => state.formsState);
  const professionals = useSelector((state) => state.forms.professionals);
  const diagnosis = useSelector((state) => state.forms.diagnosis);
  const prestacionesState = useSelector((state) => state.forms.prestaciones);
  const specialties = useSelector((state) => state.forms.specialtiesDiagnosis);
  const availableDocumentsToAdd = useSelector(
    (state) => state.accidentDashboard.availableDocumentsToAdd,
  );
  const roles = useSelector((state) => state.auth.roles);
  const time = useSelector(
    (state) => state.connectivityResponse.connectivityErrors.time,
  );
  const accident = useSelector((state) => state.accidentDashboard.accident);

  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const isRepeatForm =
    formsState.behaviour === formBehaviourEnum.REPEAT_FORM;

  const disabled = useMemo(() => {
    switch (formsState.behaviour) {
      case formBehaviourEnum.RETURN_TO_FPA:
      case formBehaviourEnum.CREATE_ACCIDENT_DOCUMENT:
      case formBehaviourEnum.REPEAT_FORM:
      case formBehaviourEnum.ERROR_DIALOG:
      case formBehaviourEnum.ERROR_CONECTIVIDAD:
        return false;
      case formBehaviourEnum.NOVEDAD_CONECTIVIDAD:
      case formBehaviourEnum.ATTENTION_IN_OTHER_PROVIDER:
        return true;
      default:
        return null;
    }
  }, [formsState.behaviour]);

  const isView = useMemo(() => {
    switch (formsState.behaviour) {
      case formBehaviourEnum.RETURN_TO_FPA:
      case formBehaviourEnum.CREATE_ACCIDENT_DOCUMENT:
      case formBehaviourEnum.REPEAT_FORM:
        return false;
      default:
        return true;
    }
  }, [formsState.behaviour]);

  // Funcion que se ejecuta despues de que el form se guarde correctamente
  const postFormCallback = useCallback(
    (formValues) => {
      switch (formsState.behaviour) {
        case formBehaviourEnum.ERROR_DIALOG:
          dispatch(
            correctConnectivityErrorAction(
              formsState.selectedDocument.idRespError,
              formsState.commonProperties.idArt,
            ),
          ).then(() => {
            dispatch(
              getDocuments(
                formsState.commonProperties.idSiniestro,
                formsState.commonProperties.idArt,
              ),
            );
            dispatch(
              getConnectivityErrors(
                formsState.selectedDocument.nroTransaccion,
                formsState.commonProperties.idArt,
              ),
            );
          });
          dispatch(
            getAppointmentsCalendar(
              formsState.commonProperties.idSiniestro,
              formsState.commonProperties.idArt,
              formsState.behaviour == formBehaviourEnum.NOVEDAD_CONECTIVIDAD ||
              formsState.behaviour ==
              formBehaviourEnum.ATTENTION_IN_OTHER_PROVIDER,
            ),
          );
          break;
        case formBehaviourEnum.ERROR_CONECTIVIDAD:
          dispatch(
            correctConnectivityErrorAction(
              formsState.selectedDocument.idRespError,
              formsState.commonProperties.idArt,
            ),
          ).then((res) => {
            dispatch(getAllConnectivityErrors(time));
          });
          break;
        case formBehaviourEnum.RETURN_TO_FPA:
        case formBehaviourEnum.CREATE_ACCIDENT_DOCUMENT:
          dispatch(
            getAvailableDocumentsToAdd(
              formsState.commonProperties.idSiniestro,
              formsState.commonProperties.idArt,
            ),
          );
          dispatch(
            getDocuments(
              formsState.commonProperties.idSiniestro,
              formsState.commonProperties.idArt,
            ),
          );
          dispatch(
            getAppointmentsCalendar(
              formsState.commonProperties.idSiniestro,
              formsState.commonProperties.idArt,
            ),
          );
          dispatch(getAccidents());
          break;
        case formBehaviourEnum.MODIFY_ACCIDENT_DOCUMENT:
        case formBehaviourEnum.ATTENTION_IN_OTHER_PROVIDER:
          dispatch(
            getDocuments(
              formsState.commonProperties.idSiniestro,
              formsState.commonProperties.idArt,
            ),
          );
          dispatch(
            getAppointmentsCalendar(
              formsState.commonProperties.idSiniestro,
              formsState.commonProperties.idArt,
              formsState.behaviour ==
              formBehaviourEnum.ATTENTION_IN_OTHER_PROVIDER,
            ),
          );
          break;
        default:
          break;
      }
    },
    [
      formsState.behaviour,
      formsState.commonProperties.idArt,
      formsState.commonProperties.idSiniestro,
      time,
      formsState.selectedDocument,
    ],
  );

  useEffect(() => {
    () => {
      // Para asegurar que el form este cerrado al desmontar el componente
      dispatch(closeForm());
    };
  }, []);

  useEffect(() => {
    // Add selectedDocument o selectedDocument como accion de redux.
    if (formsState.open) {
      setLoaded(false);
      const nroFormulario =
        formsState.selectedDocument?.nroFormulario ??
        formsState.commonProperties?.nroFormulario;
      if (nroFormulario != null) {
        dispatch(
          getForm(
            formsState.open,
            formsState.commonProperties.idArt,
            formsState.commonProperties.idSiniestro,
            nroFormulario,
          ),
        )
          .then(() => dispatch(setLoadedForm()))
          .catch(() => dispatch(setErrorLoadingForm()));
      } else {
        // Si no hay documento seleccionado estoy creando uno nuevo
        dispatch(
          getFormModel(
            formsState.open,
            formsState.commonProperties.idArt,
            formsState.commonProperties.idSiniestro,
          ),
        )
          .then(() => dispatch(setLoadedForm()))
          .catch(() => dispatch(setErrorLoadingForm()));
      }
    }
  }, [
    formsState.selectedDocument?.nroFormulario,
    formsState.commonProperties?.nroFormulario,
    formsState.commonProperties.idSiniestro,
    formsState.open,
  ]);

  const handleClose = (formSubmited, error) => {
    setLoaded(false);
    dispatch(clearIdTurnosForms());
    dispatch(clearIdTurnos());
    if (formSubmited === true) {
      if (!error) {
        !isView
          ? snackbarUtils.success("Formulario enviado correctamente")
          : snackbarUtils.success("Formulario modificado correctamente");
      }
    }
    // Close del dialog de cancelar asi no queda abierto para el proximo form que se abra
    setOpenCancelDialog(false);
    // Close del form
    dispatch(closeForm());

    switch (formsState.behaviour) {
      case formBehaviourEnum.RETURN_TO_FPA:
        dispatch(
          openNewForm(
            "fpa",
            "Formulario de Pedido de Autorización",
            {},
            undefined,
            true,
          ),
        );
    }
  };



  // Precarga de datos que requieren los formularios:
  useEffect(() => {
    if (formsState.open) {
      if (availableDocumentsToAdd.data.length === 0) {
        dispatch(
          getAvailableDocumentsToAdd(
            formsState.commonProperties.idSiniestro,
            formsState.commonProperties.idArt,
            formsState.behaviour == formBehaviourEnum.NOVEDAD_CONECTIVIDAD ||
            formsState.behaviour ==
            formBehaviourEnum.ATTENTION_IN_OTHER_PROVIDER,
          ),
        );
      }
      // Carga de turnos
      if (["iet", "iami"].includes(formsState.open)) {
        dispatch(
          getAppointmentsCalendar(
            formsState.commonProperties.idSiniestro,
            formsState.commonProperties.idArt,
            formsState.behaviour == formBehaviourEnum.NOVEDAD_CONECTIVIDAD ||
            formsState.behaviour ==
            formBehaviourEnum.ATTENTION_IN_OTHER_PROVIDER,
            formsState.commonProperties.idProvider,
          ),
        );
      }

      if (["iet", "iami"].includes(formsState.open)) {
        dispatch(
          getFormProfessionalSpecialties(formsState.commonProperties.idArt),
        );
      }

      if (
        professionals.data.length === 0 &&
        ["iet", "iami", "am", "fpa", "idr"].includes(formsState.open)
      ) {
        dispatch(getFormProfessionals(formsState.commonProperties.idArt));
      }

      if (
        diagnosis.data.length === 0 &&
        ["iet", "iami", "am"].includes(formsState.open)
      ) {
        dispatch(getFormDiagnosis(formsState.commonProperties.idArt));
      }
      if (["iet", "iami"].includes(formsState.open)) {
        dispatch(getFormNaturalezasLesion(formsState.commonProperties.idArt));
      }
      if (["iet", "iami"].includes(formsState.open)) {
        dispatch(getFormZonasAfectadas(formsState.commonProperties.idArt));
      }
      // Estos dispatch usan el comportamiento en selected document por que no se ejecutan si es nuevo documento
      if (formsState.selectedDocument) {
        if (formsState.open === "fpa" && !prestacionesState.data.prestaciones) {
          dispatch(
            getFormPrestaciones(
              formsState.commonProperties.idArt,
              formsState.behaviour == formBehaviourEnum.NOVEDAD_CONECTIVIDAD ||
              formsState.behaviour ==
              formBehaviourEnum.ATTENTION_IN_OTHER_PROVIDER,
              formsState.commonProperties.idProvider,
            ),
          );
        }

        if (formsState.open === "am" && specialties.data.length === 0) {
          dispatch(
            getFormSpecialtiesDiagnosis(formsState.commonProperties.idArt),
          );
        }
      }
    }
  }, [formsState.open, formsState.selectedDocument]);

  const changeSelectedDocument = (document) => {
    if (formsState.behaviour == formBehaviourEnum.ATTENTION_IN_OTHER_PROVIDER) {
      dispatch(
        setFormsGeneralProperties({
          idSiniestro: document.siniestro,
          idArt: accident.mlCloudCoreArtId,
          nombreTrabajador: accident.nombreTrabajador,
          apellidoTrabajador: accident.apellidoTrabajador,
          numeroHistoriaClinica: accident.siniestro,
          artName: accident.art,
          documento: accident.documento,
          idProvider: document.prestador,
        }),
      );
      dispatch(
        openEditForm(
          document.comportamiento?.trim(),
          formBehaviourEnum.ATTENTION_IN_OTHER_PROVIDER,
          {
            nroFormulario: document.nroFormulario,
          },
          {},
        ),
      );
    } else {
      if (formsState.selectedDocument) {
        dispatch(
          openEditForm(
            document.comportamiento,
            formsState.behaviour,
            {
              nroFormulario: document.nroFormulario,
            },
            {},
          ),
        );
      }
    }
  };

  const handleDialogClose = useCallback(() => {
    switch (formsState.behaviour) {
      case formBehaviourEnum.CREATE_ACCIDENT_DOCUMENT:
      case formBehaviourEnum.REPEAT_FORM:
        setOpenCancelDialog(true);
        break;
      default:
        handleClose();
    }
  }, [formsState.behaviour]);

  return (
    <Form
      isDialog={isDialog}
      openForm={formsState.open}
      handleDialogClose={handleDialogClose}
      selectedDocument={formsState.selectedDocument}
      commonProperties={formsState.commonProperties}
      handleClose={handleClose}
      formTitle={formsState.formTitle}
      behaviour={formsState.behaviour}
      documentList={
        formsState.open === "fpt" && formsState.fpt?.readOnly
          ? null
          : formsState.documentList
      }
      changeSelectedDocument={changeSelectedDocument}
      loaded={loaded}
      setLoaded={setLoaded}
      loadingForm={formsState.loadingForm}
      isView={isView}
      isRepeatForm={isRepeatForm}
      disabled={disabled}
      idSiniestro={formsState.commonProperties.idSiniestro}
      idArt={formsState.commonProperties.idArt}
      postFormCallback={postFormCallback}
      openCancelDialog={openCancelDialog}
      handleCancelFormCancelDialog={() => setOpenCancelDialog(false)}
      handleConfirmFormCancelDialog={handleClose}
      artName={formsState.commonProperties.artName}
      errorLoadingForm={formsState.errorLoadingForm}
    />
  );
};

export default FormContainer;
